import React, { useMemo } from 'react';
import {
  Search, MapPin, Package, ChevronRight, X
} from 'lucide-react';
import { StcBinDto } from '../../../../types/stcBin';
import { StcBinTypeDto } from '../../../../services/stcBinTypeService';
import { StcBinSizeDto } from '../../../../types/stcBinSize';
import { LocationFilter } from '../../../../types/fieldRegistration';
import { RackSummary } from '../rack-builder/types';

interface LocationListProps {
  bins: StcBinDto[];
  binTypes: StcBinTypeDto[];
  binSizes: StcBinSizeDto[];
  filter: LocationFilter;
  rackSummaries: RackSummary[];
  onUpdateFilter: (updates: Partial<LocationFilter>) => void;
  onSelectLocation: (bin: StcBinDto) => void;
}

export const LocationList: React.FC<LocationListProps> = ({
  bins,
  binTypes,
  binSizes,
  filter,
  rackSummaries,
  onUpdateFilter,
  onSelectLocation,
}) => {
  // Build lookup maps for display
  const binTypeMap = useMemo(() => {
    const map = new Map<number, StcBinTypeDto>();
    binTypes.forEach(bt => map.set(bt.binTypeNo, bt));
    return map;
  }, [binTypes]);

  const binSizeMap = useMemo(() => {
    const map = new Map<number, StcBinSizeDto>();
    binSizes.forEach(bs => map.set(bs.binSizeNo, bs));
    return map;
  }, [binSizes]);

  const hasActiveFilters = filter.rackPrefix || filter.binTypeNo || filter.binSizeNo || filter.zoneId;

  const clearFilters = () => {
    onUpdateFilter({
      searchTerm: '',
      rackPrefix: undefined,
      binTypeNo: undefined,
      binSizeNo: undefined,
      zoneId: undefined,
    });
  };

  return (
    <div className="space-y-3">
      {/* Search Bar */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={filter.searchTerm}
          onChange={(e) => onUpdateFilter({ searchTerm: e.target.value })}
          placeholder="Sok lokasjon..."
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] min-h-[44px]"
          autoComplete="off"
        />
      </div>

      {/* Quick Rack Filter Chips */}
      <div className="flex flex-wrap gap-2">
        {rackSummaries.map((rack) => (
          <button
            key={rack.rackNumber}
            onClick={() => onUpdateFilter({
              rackPrefix: filter.rackPrefix === rack.rackNumber ? undefined : rack.rackNumber
            })}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors min-h-[32px] ${
              filter.rackPrefix === rack.rackNumber
                ? 'bg-[#0E7C86] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Reol {rack.rackNumber}
            <span className="ml-1 opacity-70">({rack.locationCount})</span>
          </button>
        ))}

        {/* Filter dropdowns for type/size */}
        <select
          value={filter.binTypeNo || ''}
          onChange={(e) => onUpdateFilter({ binTypeNo: e.target.value ? Number(e.target.value) : undefined })}
          className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border-none min-h-[32px] cursor-pointer"
        >
          <option value="">Alle typer</option>
          {binTypes.map(bt => (
            <option key={bt.binTypeNo} value={bt.binTypeNo}>
              {bt.description1 || `Type ${bt.binTypeNo}`}
            </option>
          ))}
        </select>

        <select
          value={filter.binSizeNo || ''}
          onChange={(e) => onUpdateFilter({ binSizeNo: e.target.value ? Number(e.target.value) : undefined })}
          className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border-none min-h-[32px] cursor-pointer"
        >
          <option value="">Alle storr.</option>
          {binSizes.map(bs => (
            <option key={bs.binSizeNo} value={bs.binSizeNo}>
              {bs.description1 || `Str. ${bs.binSizeNo}`}
            </option>
          ))}
        </select>
      </div>

      {/* Active Filter Clear */}
      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className="flex items-center gap-1 text-xs text-[#0E7C86] hover:text-[#0A5F66] font-medium"
        >
          <X size={12} />
          Fjern filtre ({bins.length} lokasjoner)
        </button>
      )}

      {/* Results Count */}
      <p className="text-xs text-gray-500 px-1">
        Viser {bins.length} lokasjon{bins.length !== 1 ? 'er' : ''}
      </p>

      {/* Location Cards */}
      <div className="space-y-2">
        {bins.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-6 text-center">
            <MapPin size={24} className="mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-500">Ingen lokasjoner funnet</p>
          </div>
        ) : (
          bins.map((bin) => {
            const binType = binTypeMap.get(bin.binTypeNo);
            const binSize = binSizeMap.get(bin.binSizeNo);

            return (
              <button
                key={`${bin.warehouseID}-${bin.binCode}`}
                onClick={() => onSelectLocation(bin)}
                className="w-full bg-white rounded-lg border border-gray-200 hover:border-[#0E7C86] hover:shadow-sm p-3 text-left transition-all active:scale-[0.99] min-h-[60px]"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Bin Code - primary */}
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-[#0E7C86] flex-shrink-0" />
                      <span className="font-semibold text-sm text-gray-900 truncate">
                        {bin.binCode}
                      </span>
                      {bin.description1 && bin.description1 !== bin.binCode && (
                        <span className="text-xs text-gray-400 truncate">
                          ({bin.description1})
                        </span>
                      )}
                    </div>

                    {/* Type + Size info */}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500 truncate">
                        {binType?.description1 || `Type ${bin.binTypeNo}`}
                      </span>
                      <span className="text-xs text-gray-300">|</span>
                      <span className="text-xs text-gray-500 truncate">
                        {binSize?.description1 || `Str. ${bin.binSizeNo}`}
                      </span>
                      {bin.stcZoneID && (
                        <>
                          <span className="text-xs text-gray-300">|</span>
                          <span className="text-xs text-gray-500">
                            Sone {bin.stcZoneID}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <ChevronRight size={18} className="text-gray-400 flex-shrink-0 ml-2" />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
